<script lang="ts" setup>
import IconSxTelegram from '~/components/sx/icons/IconSxTelegram.vue';
import IconSxMail from '~/components/sx/icons/IconSxMail.vue';
import IconSxChat from "~/components/sx/icons/IconSxChat.vue";
import {usePartnerConfig} from "~/stores/usePartnerConfig";
import IconSxWechat from "~/components/sx/icons/IconSxWechat.vue";
import { onClickOutside } from '@vueuse/core';
import IconSxWhatsApp from "~/components/sx/icons/IconSxWhatsApp.vue";
import {ref} from "vue";

const { supportEmail, telegramLink, qrValue, whatsappLink } = usePartnerConfig();

const visibleCode = ref<"wechat" | "whatsapp" | null>(null);
const qrPosition = ref({ top: 0, left: 0 });

const wechatTrigger = ref<HTMLElement | null>(null);
const whatsappTrigger = ref<HTMLElement | null>(null);

// TODO: вынести в конфиг или .env
const ticketUrl = computed(() => {
  return `https://my.sx.org/chat/ticket-create`;
})

const toggleCode = async (target: "wechat" | "whatsapp") => {
  if (visibleCode.value === target) {
    visibleCode.value = null;
    return;
  }

  visibleCode.value = target;

  await nextTick();
  const el = target === "wechat" ? wechatTrigger.value : whatsappTrigger.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    qrPosition.value = {
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + rect.width / 2 + window.scrollX,
    };
  }
};

onMounted(async () => {
  onClickOutside(wechatTrigger.value, () => {
    if (wechatTrigger.value) {
      visibleCode.value = null;
    }
  });
});
</script>

<template>
  <section class="sx-support">
    <div class="sx-support__inner">
      <div class="sx-support__section">
        <img src="/sx/Support.webp" alt="support-img" ref="image" class="hidden" loading="lazy">
      </div>

      <div class="sx-support__section sx-support__section--right">
        <h2 class="sx-landing-title sx-support__title">
          {{ $t('components.support.title') }} 24/7
        </h2>

        <p class="sx-support__desc">
          {{ $t('components.support.description') }}
        </p>

        <div class="sx-support__contacts">
          <div class="sx-support__contacts-inner">
            <a :href="telegramLink" target='_blank' class="sx-support__contacts-item default-tag">
              <icon-sx-telegram />
              <span>Telegram</span>
            </a>
            <span ref="whatsappTrigger" @click="toggleCode('whatsapp')" class="sx-support__contacts-item default-tag">
              <icon-sx-whats-app />
              <span>WhatsApp</span>
            </span>
            <span ref="wechatTrigger" @click="toggleCode('wechat')" class="sx-support__contacts-item default-tag">
             <icon-sx-wechat />
              <span>WeChat</span>
            </span>
          </div>
          <div class="sx-support__contacts-inner lg">
            <a :href="ticketUrl" target='_blank' class="sx-support__contacts-item lg default-tag">
              <icon-sx-chat />
              <span>{{ $t('components.support.createTicket') }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <teleport to="body">
      <div
        v-if="visibleCode"
        class="qr-code"
        :style="{ top: qrPosition.top + 'px', left: qrPosition.left + 'px' }"
      >
        <AppQrCode
          :link="visibleCode === 'wechat' ? qrValue : whatsappLink"
          :options="{ width: 132, height: 132, errorCorrectionLevel: 'H', margin: 2 }"
        />
      </div>
    </teleport>
  </section>
</template>

<style lang="scss" scoped>
.qr-code {
  position: absolute;
  transform: translateX(-50%);
  transition: 0.3s;
  z-index: 9999;
}

.sx-support {
  min-height: 100vh;
  background: #F6FFFF;
  display: flex;

  &__inner {
    display: flex;
    align-items: center;
    max-width: 1069px;
    margin: 0 auto;
    padding: 2rem;
    gap: 64px;
    justify-content: space-between;
    @media (max-width: 1199px) {
      flex-direction: column;
      gap: 1rem;
    }
  }

  &__section {
    height: 100%;
    max-width: 450px;
    @media (max-width: 599px) {
      max-width: 64vw;
    }

    img {
      width: 100%;
      object-fit: contain;
      height: 100%;
    }

    &--right {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      max-width: inherit;
    }
  }

  &__title {
    font-size: 52px;
    font-weight: 900;
    line-height: 52px;
    color: #000;
  }

  &__desc {
    max-width: 480px;
    margin: 24px 0;
    font-size: 18px;
    font-weight: 400;
    line-height: 24px;
    color: #1A1C1F;
  }

  &__contacts {
    display: flex;
    flex-wrap: wrap;
    max-width: 460px;
    gap: 12px;

    @media (max-width: 1199px) {
      flex-wrap: wrap;
      justify-content: center;
    }

    &-inner {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      width: 100%;
      gap: 8px;
    }
    &-item {
      width: fit-content;
      display: flex;
      gap: 7px;
      align-items: center;
      justify-content: center;
      height: 42px;
      padding: 0 24px;
      border-radius: 30px;
      outline: 1px solid #000000;
      color: #000000;
      cursor: pointer;
      position: relative;
      @media (max-width: 899px) {
        width: 100%;
      }
      &.lg {
        width: 100%;
      }
      span {
        white-space: nowrap;
        font-size: 16px;
        font-weight: 500;
        line-height: 42px;
      }
      &:hover {
        background: #000000;
        color: #fff;

        :deep(svg path) {
          fill: #fff;
        }
      }
    }
  }
}
</style>
